import { P, Stack } from "@introist/react-foundation/v2";
import styled from "styled-components";
import { CompletionSource } from "@codemirror/autocomplete";

type FieldDoc = { name: string; description: string };

const Code = styled.code`
  background: var(--palette-surface-dimmed);
  border-radius: 4px;
  padding: 1px 5px;
  font-family: monospace;
  font-size: 0.85em;
`;

const Text = styled(P)`
  margin: 0;
`;

const Dimmed = styled(Text)`
  color: var(--palette-foreground-subdued);
`;

const eventFields: FieldDoc[] = [
  {
    name: "event",
    description: 'event key, e.g. "employee-changed" or "dataset-entry-changed:<id>"'
  },
  {
    name: "employeeId",
    description:
      "set when the source event already has one (e.g. employee events); undefined otherwise"
  },
  {
    name: "payload",
    description:
      'event-specific data, e.g. the created/changed record. "Changed" events also include a changes array of { type, path, original, modified }'
  },
  { name: "resources", description: "related resource ids, e.g. the dataset entry" }
];

const triggerFields: FieldDoc[] = [
  { name: "id", description: "this trigger's id" },
  { name: "name", description: "this trigger's name" },
  { name: "workflowId", description: "the workflow this trigger starts" },
  { name: "triggerEvent", description: "the selected event config" },
  { name: "employeeGroupId", description: "the employee group filter, if any" }
];

const employeeFields: FieldDoc[] = [
  { name: "id", description: "employee id" },
  { name: "name", description: "full name" },
  { name: "firstname", description: "first name" },
  { name: "lastname", description: "last name" }
];

const FieldList = ({ subject, fields, note }: { subject: string; fields: FieldDoc[]; note?: string }) => (
  <Stack vertical gap="xSmall">
    <Text style={{ fontWeight: 600 }}>{subject}</Text>
    <Stack vertical gap="small">
      {fields.map(f => (
        <Stack key={f.name} vertical gap="none">
          <Text>
            <Code>
              {subject}.{f.name}
            </Code>
          </Text>
          <Dimmed>{f.description}</Dimmed>
        </Stack>
      ))}
    </Stack>
    {note && <Dimmed style={{ fontStyle: "italic" }}>{note}</Dimmed>}
  </Stack>
);

const EventShape = () => <FieldList subject="event" fields={eventFields} />;

const TriggerShape = () => <FieldList subject="trigger" fields={triggerFields} />;

const EmployeeShape = () => (
  <FieldList
    subject="employee"
    fields={employeeFields}
    note="Plus every custom employee field as a top-level key, e.g. employee.email."
  />
);

const buildDotCompletion = (param: string, fields: FieldDoc[]): CompletionSource => context => {
  const match = context.matchBefore(new RegExp(`${param}\\.\\w*`));
  if (!match) return null;

  return {
    from: match.from + param.length + 1,
    options: fields.map(f => ({ label: f.name, type: "property" })),
    validFor: /^\w*$/
  };
};

const eventCompletion = buildDotCompletion("event", eventFields);
const triggerCompletion = buildDotCompletion("trigger", triggerFields);
const employeeCompletion = buildDotCompletion("employee", employeeFields);

export const employeeFnHelp = (
  <Stack vertical gap="medium">
    <Text>
      Receives <Code>{"{ event, trigger }"}</Code>. Return the id of the employee to run the
      automation for, or nothing to skip.
    </Text>
    <EventShape />
    <TriggerShape />
  </Stack>
);
export const employeeFnCompletions = [eventCompletion, triggerCompletion];

export const filterFnHelp = (
  <Stack vertical gap="medium">
    <Text>
      Receives <Code>{"{ event, trigger, employee }"}</Code>. Return true to continue, a falsy
      value to skip, or a string to skip with that message as the reason.
    </Text>
    <EventShape />
    <TriggerShape />
    <EmployeeShape />
  </Stack>
);
export const filterFnCompletions = [eventCompletion, triggerCompletion, employeeCompletion];

export const preprocessFnHelp = (
  <Stack vertical gap="medium">
    <Text>
      Receives <Code>{"{ event, employee, trigger }"}</Code>. Return an object to merge into the
      automation's data, or null.
    </Text>
    <EventShape />
    <EmployeeShape />
    <TriggerShape />
  </Stack>
);
export const preprocessFnCompletions = [eventCompletion, employeeCompletion, triggerCompletion];

export const postprocessFnHelp = (
  <Stack vertical gap="medium">
    <Text>
      Receives <Code>{"{ employee, trigger }"}</Code>. Return an object to merge into the
      automation's data, or null.
    </Text>
    <EmployeeShape />
    <TriggerShape />
  </Stack>
);
export const postprocessFnCompletions = [employeeCompletion, triggerCompletion];
